import React, { useState } from 'react';
import {
  View, Text, TextInput, TouchableOpacity,
  StyleSheet, KeyboardAvoidingView, Platform, ActivityIndicator, Alert,
} from 'react-native';
import { useDispatch } from 'react-redux';
import { colors, fonts, RD } from '../../theme/colors';
import { LogoLockup } from '../../components/Logo';
import { api } from '../../services/api';
import { setToken, setDriver } from '../../store';

const showAlert = (title: string, message: string) => {
  if (Platform.OS === 'web' && typeof window !== 'undefined') {
    window.alert(`${title}\n\n${message}`);
  } else {
    Alert.alert(title, message);
  }
};

export function LoginScreen({ navigation }: any) {
  const dispatch = useDispatch();
  const [email,    setEmail]    = useState('');
  const [password, setPassword] = useState('');
  const [error,    setError]    = useState('');
  const [loading,  setLoading]  = useState(false);
  const [showPw,   setShowPw]   = useState(false);

  // Forgot password state
  const [forgotMode, setForgotMode] = useState<'off' | 'email' | 'code'>('off');
  const [forgotEmail, setForgotEmail] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotError, setForgotError] = useState('');
  const [forgotSuccess, setForgotSuccess] = useState('');

  const handleLogin = async () => {
    setLoading(true); setError('');
    if (!email || !password) {
      setError('Please enter your email and password.');
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.post('/auth/login', { email, password });
      dispatch(setToken(data.token));
      dispatch(setDriver(data.driver));
    } catch (err: any) {
      const msg = err?.response?.data?.error || 'Unable to connect. Please check your connection and try again.';
      setError(msg);
      setLoading(false);
      return;
    } finally {
      setLoading(false);
    }
  };

  const handleForgotSubmitEmail = async () => {
    if (!forgotEmail.trim()) {
      setForgotError('Please enter your email address.');
      return;
    }
    setForgotLoading(true);
    setForgotError('');
    try {
      const baseUrl = (api.defaults as any).baseURL || '';
      const resp = await fetch(`${baseUrl}/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: forgotEmail.trim() }),
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.error || 'Failed');
      setForgotMode('code');
      setForgotSuccess('A reset code has been sent to your phone number on file.');
    } catch (err: any) {
      setForgotError(err.message || 'Something went wrong.');
    } finally {
      setForgotLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (!resetCode.trim() || !newPassword.trim()) {
      setForgotError('Please enter the code and your new password.');
      return;
    }
    if (newPassword.length < 8) {
      setForgotError('Password must be at least 8 characters.');
      return;
    }
    setForgotLoading(true);
    setForgotError('');
    try {
      const baseUrl = (api.defaults as any).baseURL || '';
      const resp = await fetch(`${baseUrl}/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: forgotEmail.trim(), code: resetCode.trim(), newPassword }),
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.error || 'Failed');
      setForgotMode('off');
      setForgotEmail('');
      setResetCode('');
      setNewPassword('');
      setForgotSuccess('');
      showAlert('Password Reset', 'Your password has been updated. Please log in with your new password.');
    } catch (err: any) {
      setForgotError(err.message || 'Something went wrong.');
    } finally {
      setForgotLoading(false);
    }
  };

  // Forgot password UI
  if (forgotMode !== 'off') {
    return (
      <KeyboardAvoidingView
        style={s.container}
        behavior={Platform.OS === 'ios' ? 'padding' : undefined}
      >
        <View style={s.inner}>
          <View style={s.card}>
            <View style={s.logoArea}>
              <LogoLockup />
              <Text style={s.tagline}>Reset Password</Text>
            </View>

            <View style={s.form}>
              {!!forgotError && (
                <View style={s.errorBox}>
                  <Text style={s.errorText}>{forgotError}</Text>
                </View>
              )}
              {!!forgotSuccess && (
                <View style={s.successBox}>
                  <Text style={s.successText}>{forgotSuccess}</Text>
                </View>
              )}

              {forgotMode === 'email' && (
                <>
                  <View style={s.field}>
                    <Text style={s.label}>Email Address</Text>
                    <TextInput
                      style={s.input}
                      placeholder="you@email.com"
                      value={forgotEmail}
                      onChangeText={setForgotEmail}
                      autoCapitalize="none"
                      keyboardType="email-address"
                      autoComplete="off"
                      placeholderTextColor={RD.textFaint}
                    />
                    <Text style={s.hintText}>We'll send a reset code to the phone number on your account.</Text>
                  </View>
                  <TouchableOpacity
                    style={[s.btn, forgotLoading && s.btnDisabled]}
                    onPress={handleForgotSubmitEmail}
                    disabled={forgotLoading}
                  >
                    {forgotLoading
                      ? <ActivityIndicator color={colors.white} />
                      : <Text style={s.btnText}>Send Reset Code</Text>}
                  </TouchableOpacity>
                </>
              )}

              {forgotMode === 'code' && (
                <>
                  <View style={s.field}>
                    <Text style={s.label}>Reset Code</Text>
                    <TextInput
                      style={s.input}
                      placeholder="Enter 6-digit code"
                      value={resetCode}
                      onChangeText={setResetCode}
                      keyboardType="number-pad"
                      autoComplete="off"
                      placeholderTextColor={RD.textFaint}
                    />
                  </View>
                  <View style={s.field}>
                    <Text style={s.label}>New Password</Text>
                    <TextInput
                      style={s.input}
                      placeholder="Min. 8 characters"
                      value={newPassword}
                      onChangeText={setNewPassword}
                      secureTextEntry
                      autoComplete="new-password"
                      placeholderTextColor={RD.textFaint}
                    />
                  </View>
                  <TouchableOpacity
                    style={[s.btn, forgotLoading && s.btnDisabled]}
                    onPress={handleResetPassword}
                    disabled={forgotLoading}
                  >
                    {forgotLoading
                      ? <ActivityIndicator color={colors.white} />
                      : <Text style={s.btnText}>Reset Password</Text>}
                  </TouchableOpacity>
                </>
              )}

              <TouchableOpacity
                onPress={() => { setForgotMode('off'); setForgotError(''); setForgotSuccess(''); }}
                style={s.linkWrap}
              >
                <Text style={s.link}>
                  Back to{' '}
                  <Text style={s.linkBold}>Log in</Text>
                </Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </KeyboardAvoidingView>
    );
  }

  return (
    <KeyboardAvoidingView
      style={s.container}
      behavior={Platform.OS === 'ios' ? 'padding' : undefined}
    >
      <View style={s.inner}>
        <View style={s.card}>
          <View style={s.logoArea}>
            <LogoLockup />
            <Text style={s.tagline}>Driver Platform</Text>
          </View>

          <View style={s.form}>
            {!!error && (
              <View style={s.errorBox}>
                <Text style={s.errorText}>{error}</Text>
              </View>
            )}

            <View style={s.field}>
              <Text style={s.label}>Email</Text>
              <TextInput
                style={s.input}
                placeholder="you@email.com"
                value={email}
                onChangeText={setEmail}
                autoCapitalize="none"
                keyboardType="email-address"
                autoComplete="off"
                placeholderTextColor={RD.textFaint}
              />
            </View>

            <View style={s.field}>
              <Text style={s.label}>Password</Text>
              <View style={s.pwRow}>
                <TextInput
                  style={s.pwInput}
                  placeholder="Password"
                  value={password}
                  onChangeText={setPassword}
                  secureTextEntry={!showPw}
                  autoComplete="new-password"
                  placeholderTextColor={RD.textFaint}
                />
                <TouchableOpacity onPress={() => setShowPw(!showPw)} style={s.showBtn}>
                  <Text style={s.showBtnText}>{showPw ? 'Hide' : 'Show'}</Text>
                </TouchableOpacity>
              </View>
            </View>

            <TouchableOpacity
              style={[s.btn, loading && s.btnDisabled]}
              onPress={handleLogin}
              disabled={loading}
            >
              {loading
                ? <ActivityIndicator color={colors.white} />
                : <Text style={s.btnText}>Log In</Text>}
            </TouchableOpacity>

            <TouchableOpacity
              onPress={() => { setForgotMode('email'); setForgotEmail(email); setForgotError(''); }}
              style={s.linkWrap}
            >
              <Text style={[s.link, { color: RD.maroon }]}>Forgot my password</Text>
            </TouchableOpacity>

            <TouchableOpacity onPress={() => navigation.navigate('Register')} style={s.linkWrap}>
              <Text style={s.link}>
                New driver?{' '}
                <Text style={s.linkBold}>Create account</Text>
              </Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </KeyboardAvoidingView>
  );
}

const MAX_WIDTH = 440;

const s = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: RD.bg,
  },
  inner: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 24,
  },
  card: {
    width: '100%',
    maxWidth: MAX_WIDTH,
    backgroundColor: RD.surface,
    borderRadius: 18,
    padding: 36,
    shadowColor: '#000',
    shadowOpacity: 0.04,
    shadowRadius: 3,
    elevation: 1,
    borderWidth: 1,
    borderColor: RD.border,
  },
  logoArea: {
    alignItems: 'center',
    marginBottom: 40,
    gap: 8,
  },
  tagline: {
    fontSize: 12,
    color: RD.textFaint,
    fontFamily: fonts.medium,
    letterSpacing: 1.5,
    textTransform: 'uppercase',
  },
  form: {
    gap: 18,
  },
  field: {
    gap: 6,
  },
  label: {
    fontSize: 14,
    fontFamily: fonts.semiBold,
    color: RD.text,
  },
  hintText: {
    fontSize: 13,
    color: RD.textFaint,
    fontFamily: fonts.regular,
    marginTop: 2,
  },
  input: {
    backgroundColor: RD.surface,
    borderRadius: 14,
    paddingHorizontal: 16,
    paddingVertical: 14,
    fontSize: 15,
    borderWidth: 1.5,
    borderColor: RD.borderStrong,
    color: RD.text,
    fontFamily: fonts.regular,
  },
  pwRow: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: RD.surface,
    borderRadius: 14,
    borderWidth: 1.5,
    borderColor: RD.borderStrong,
  },
  pwInput: {
    flex: 1,
    paddingHorizontal: 16,
    paddingVertical: 14,
    fontSize: 15,
    color: RD.text,
    fontFamily: fonts.regular,
  },
  showBtn: {
    paddingHorizontal: 14,
    paddingVertical: 14,
  },
  showBtnText: {
    fontSize: 13,
    color: RD.textMuted,
    fontFamily: fonts.semiBold,
  },
  btn: {
    backgroundColor: RD.ink,
    borderRadius: 16,
    height: 54,
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: 4,
  },
  btnDisabled: {
    backgroundColor: RD.g30,
  },
  btnText: {
    color: '#fff',
    fontFamily: fonts.semiBold,
    fontSize: 16,
    fontWeight: '600',
  },
  linkWrap: {
    alignItems: 'center',
    paddingVertical: 4,
  },
  link: {
    fontSize: 14,
    color: RD.textMuted,
    fontFamily: fonts.regular,
  },
  linkBold: {
    color: RD.maroon,
    fontFamily: fonts.semiBold,
  },
  errorBox: {
    backgroundColor: RD.dangerSoft,
    borderRadius: 12,
    padding: 12,
    borderWidth: 1,
    borderColor: RD.danger,
  },
  errorText: {
    color: RD.danger,
    fontSize: 14,
    fontFamily: fonts.medium,
    textAlign: 'center',
  },
  successBox: {
    backgroundColor: RD.moneySoft,
    borderRadius: 12,
    padding: 12,
    borderWidth: 1,
    borderColor: RD.money,
  },
  successText: {
    color: RD.money,
    fontSize: 14,
    fontFamily: fonts.medium,
    textAlign: 'center',
  },
});
